import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Avatar,
  LinearProgress,
  IconButton,
  Alert,
  Paper,
} from '@mui/material';
import {
  CameraAlt,
  Delete,
  CloudUpload,
} from '@mui/icons-material';
import api from '../api';

/**
 * Professional Profile Picture Upload Component
 * Supports image preview, drag-and-drop, and validation
 */
const ProfilePictureUpload = ({ candidateId, existingPicture, onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(existingPicture?.url || '');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const maxSize = 2 * 1024 * 1024; // 2MB

  const validateFile = (file) => {
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload JPG, PNG, or WEBP images only.');
      return false;
    }

    if (file.size > maxSize) {
      setError('File size exceeds 2MB limit.');
      return false;
    }

    return true;
  };

  const handleFileSelect = (file) => {
    setError('');
    setSuccess('');

    if (validateFile(file)) {
      setSelectedFile(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError('');
    setSuccess('');
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('profile_picture', selectedFile);

    try {
      const response = await api.post('/candidate/upload-profile-picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      if (response.data.success) {
        setSuccess('Profile picture uploaded successfully!');
        setSelectedFile(null);
        if (onUploadSuccess) {
          onUploadSuccess(response.data.data);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload profile picture');
      // Reset preview on error
      setPreviewUrl(existingPicture?.url || '');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async () => {
    if (!existingPicture) return;

    try {
      const response = await api.post('/candidate/delete-file', {
        path: existingPicture.path,
        type: 'profile_picture',
      });

      if (response.data.success) {
        setSuccess('Profile picture deleted successfully');
        setPreviewUrl('');
        if (onUploadSuccess) {
          onUploadSuccess(null);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete profile picture');
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setPreviewUrl(existingPicture?.url || '');
  };

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1.5,
          color: 'text.secondary',
          fontWeight: 500,
          fontSize: '0.875rem',
        }}
      >
        Profile Picture (JPG, PNG, WEBP - Max 2MB)
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Paper
          elevation={0}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          sx={{
            p: 3,
            border: '2px dashed',
            borderColor: dragActive ? 'primary.main' : 'divider',
            borderRadius: 2,
            backgroundColor: dragActive ? 'action.hover' : 'background.paper',
            cursor: 'pointer',
            transition: 'all 0.3s',
            textAlign: 'center',
            width: '100%',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover',
            },
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />

          <Avatar
            src={previewUrl}
            sx={{
              width: 150,
              height: 150,
              margin: '0 auto',
              mb: 2,
              border: '4px solid',
              borderColor: 'divider',
            }}
          >
            <CameraAlt sx={{ fontSize: 60 }} />
          </Avatar>

          <Typography variant="body1" gutterBottom fontWeight={600}>
            {previewUrl ? 'Click to change picture' : 'Click to upload picture'}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            or drag and drop
          </Typography>

          <Typography variant="caption" color="text.secondary">
            JPG, PNG, WEBP (Max 2MB)
          </Typography>
        </Paper>

        {selectedFile && (
          <Box sx={{ width: '100%' }}>
            {uploading && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}
                >
                  Uploading... {uploadProgress}%
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={uploading}
                fullWidth
                startIcon={<CloudUpload />}
              >
                {uploading ? 'Uploading...' : 'Upload Picture'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={uploading}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {previewUrl && !selectedFile && existingPicture && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleDelete}
            startIcon={<Delete />}
            fullWidth
          >
            Delete Picture
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ProfilePictureUpload;
